/*!
 * Skyplus audit — lightweight client for recording application-level audit events
 * (currently: a user opening a document in the viewer) into the unified audit store,
 * so the Auditor's "Document Activity" report can show who viewed what.
 *
 * Fire-and-forget and best-effort: a failure here must never affect the user's action.
 */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';

@Injectable({ providedIn: 'root' })
export class AcaAuditService {
  private readonly base: string;
  private lastId = '';
  private lastAt = 0;

  constructor(private http: HttpClient, private auth: AuthenticationService, appConfig: AppConfigService) {
    // Same origin the workflow API uses; '/audit' resolves to the backend '/api/audit'.
    this.base = `${appConfig.get<string>('skyplus.apiUrl', '/bpm/api')}/audit`;
  }

  /** Record that the current user opened a document in the viewer. */
  recordView(node: { id?: string; name?: string; path?: { name?: string } }): void {
    if (!node?.id) {
      return;
    }
    // De-dupe rapid double navigations to the same node.
    const now = Date.now();
    if (node.id === this.lastId && now - this.lastAt < 4000) {
      return;
    }
    this.lastId = node.id;
    this.lastAt = now;

    const token = this.auth.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const body = {
      action: 'DOCUMENT_VIEW',
      objectType: 'document',
      objectId: node.id,
      objectName: node.name,
      objectPath: node.path?.name
    };
    this.http.post(`${this.base}/event`, body, { headers }).subscribe({
      next: () => {},
      error: () => {} // best-effort; never surface to the user
    });
  }
}
